import type { DragPayload } from "@frabr/fabric-editor";
import { mediumMeta } from "./composition_document";

const MIME = "application/x-toolbox-item";

export interface ToolboxItemData {
  type: string;
  url?: string;
  id?: number;
  kind?: string;
  media_type?: string;
  // Text items
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: string;
  // Shape items — paths inline (formes de catalogue, polychromes ou non) : la clé
  // traverse littéralement jusqu'à ShapeLayerOptions.paths de l'éditeur.
  shapeType?: string;
  paths?: { d: string; fill?: string }[];
}

/**
 * Convert a toolbox item payload (toolbox-item:dragstart / :selected detail)
 * into a DragPayload. The button's payload-value flows through literally —
 * any TextLayerOptions / ShapeLayerOptions key declared on the button
 * (text, fontSize, fontWeight, fill, width…) reaches the created object.
 * `defaults` fill in app-level values without overriding the button's keys.
 */
export function toDragPayload(
  data: ToolboxItemData,
  defaults: { text?: string; fill?: string } = {}
): DragPayload | null {
  switch (data.type) {
    case "text": {
      const { type: _t, ...opts } = data;
      return {
        kind: "text",
        opts: { ...(defaults.text != null ? { text: defaults.text } : {}), ...opts },
      };
    }
    case "shape": {
      const { type: _s, shapeType, ...opts } = data;
      if (!shapeType) return null;
      return {
        kind: "shape",
        shapeType,
        opts: { ...(defaults.fill != null ? { fill: defaults.fill } : {}), ...opts },
      };
    }
    // Le média part avec son identité : le calque posé porte `image.content_medium_id`.
    case "media":
      return data.url ? { kind: "image", url: data.url, opts: { imageMeta: mediumMeta(data) } } : null;
    // Une image à fournir : se lâche comme une image (cf. DropHandler userSlot)
    case "userSlot": {
      const { type: _u, ...opts } = data;
      return { kind: "userSlot", opts };
    }
    default:
      return null;
  }
}

interface ToolboxDropZoneCallbacks {
  onDragOver?: (e: DragEvent) => void;
  onDrop?: (e: DragEvent, data: ToolboxItemData) => void;
  onDragEnd?: (e: DragEvent) => void;
  onLeave?: () => void;
  /**
   * Enable the HTML drag ghost (clone of the dragged item following the
   * cursor). The ghost is hidden while the cursor is over this element —
   * pass the canvas area, where the editor manifests its own live preview.
   */
  ghostZone?: HTMLElement;
}

// Note: there is deliberately no onDragStart callback. A document-level
// native dragstart listener fires (capture) BEFORE the item's Stimulus
// handler calls dataTransfer.setData, so a MIME filter can never pass at
// that point. Anything that must react to a toolbox drag starting should
// listen to the "toolbox-item:dragstart" custom event instead, which
// bubbles after the payload is set and carries it in detail.

/**
 * HTML clone of a dragged toolbox item that follows the cursor.
 *
 * Replaces the native browser drag ghost (suppressed via setDragImage in
 * toolbox_item_controller) so the preview can be hidden once the item
 * manifests as a live Fabric object over the canvas — something the
 * native ghost can't do (setDragImage only works at dragstart).
 */
class DragGhost {
  private el: HTMLElement | null = null;
  private base = { left: 0, top: 0 };
  private boundMove = (evt: Event) => this.move(evt as DragEvent);

  /** @param hideZone Element over which the ghost is hidden (the canvas area). */
  constructor(private hideZone: HTMLElement) {}

  start(source: HTMLElement): void {
    this.end();
    // Clone only the visual content ([data-drag-ghost]) when the item marks
    // one — not the whole button with its border and padding.
    const visual = source.querySelector<HTMLElement>("[data-drag-ghost]") ?? source;
    const el = visual.cloneNode(true) as HTMLElement;
    const rect = visual.getBoundingClientRect();
    el.style.position = "fixed";
    el.style.left = "-9999px";
    el.style.top = "-9999px";
    el.style.width = `${rect.width}px`;
    el.style.height = `${rect.height}px`;
    el.style.margin = "0";
    el.style.opacity = "0.7";
    el.style.pointerEvents = "none";
    el.style.zIndex = "10000";
    el.style.transform = "translate(-50%, -50%)";
    // A <dialog> renders in the browser's top layer, above any z-index:
    // if the item lives in one, the ghost must be mounted inside it too.
    const mount = source.closest("dialog") ?? document.body;
    mount.appendChild(el);
    // If the mount carries a transform (e.g. a slide-in animation kept by
    // fill:forwards), it becomes the containing block for our fixed
    // positioning — compensate viewport coordinates by its offset.
    const hasTransform = mount !== document.body && getComputedStyle(mount).transform !== "none";
    const mountRect = hasTransform ? mount.getBoundingClientRect() : { left: 0, top: 0 };
    this.base = { left: mountRect.left, top: mountRect.top };
    this.el = el;
    document.addEventListener("dragover", this.boundMove, true);
  }

  private move(e: DragEvent): void {
    if (!this.el) return;
    const overZone = e.target instanceof Node && this.hideZone.contains(e.target);
    this.el.style.display = overZone ? "none" : "";
    this.el.style.left = `${e.clientX - this.base.left}px`;
    this.el.style.top = `${e.clientY - this.base.top}px`;
  }

  end(): void {
    document.removeEventListener("dragover", this.boundMove, true);
    this.el?.remove();
    this.el = null;
  }
}

/**
 * Handles the boilerplate of receiving toolbox drag & drop events:
 * filtering by MIME type, parsing JSON payload, attaching/detaching
 * capture-phase listeners, and the optional HTML drag ghost.
 *
 * Usage:
 *   const zone = new ToolboxDropZone(element, {
 *     onDragOver(e) { ... },
 *     onDrop(e, data) { ... },
 *     onLeave() { ... },
 *   });
 *   zone.destroy();
 */
export class ToolboxDropZone {
  private el: HTMLElement;
  private ghost: DragGhost | null = null;
  private boundDragOver: (e: Event) => void;
  private boundDrop: (e: Event) => void;
  private boundDragLeave: (e: Event) => void;
  private boundDocDragEnd: ((e: Event) => void) | null = null;
  private boundItemDragStart: ((e: Event) => void) | null = null;

  constructor(element: HTMLElement, callbacks: ToolboxDropZoneCallbacks) {
    this.el = element;

    this.boundDragOver = (evt: Event) => {
      const e = evt as DragEvent;
      if (!e.dataTransfer?.types.includes(MIME)) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer!.dropEffect = "copy";
      callbacks.onDragOver?.(e);
    };

    this.boundDrop = (evt: Event) => {
      const e = evt as DragEvent;
      if (!e.dataTransfer?.types.includes(MIME)) return;
      let data: ToolboxItemData;
      try { data = JSON.parse(e.dataTransfer!.getData(MIME)); } catch { return; }
      if (!data?.type) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      callbacks.onDrop?.(e, data);
    };

    this.boundDragLeave = (evt: Event) => {
      const e = evt as DragEvent;
      if (!element.contains(e.relatedTarget as Node)) {
        callbacks.onLeave?.();
      }
    };

    element.addEventListener("dragover", this.boundDragOver, true);
    element.addEventListener("drop", this.boundDrop, true);
    element.addEventListener("dragleave", this.boundDragLeave, true);

    if (callbacks.ghostZone) {
      this.ghost = new DragGhost(callbacks.ghostZone);
      // Driven by the custom event, not native dragstart — see note above.
      this.boundItemDragStart = (evt: Event) => {
        const target = evt.target;
        if (!(target instanceof HTMLElement)) return;
        // Clone the whole toolbox item, not the inner child the drag started on
        const item = (target.closest('[draggable="true"]') as HTMLElement) ?? target;
        this.ghost!.start(item);
      };
      document.addEventListener("toolbox-item:dragstart", this.boundItemDragStart);
    }

    if (callbacks.onDragEnd || this.ghost) {
      this.boundDocDragEnd = (evt: Event) => {
        this.ghost?.end();
        callbacks.onDragEnd?.(evt as DragEvent);
      };
      document.addEventListener("dragend", this.boundDocDragEnd, true);
    }
  }

  destroy(): void {
    this.ghost?.end();
    if (this.boundItemDragStart) document.removeEventListener("toolbox-item:dragstart", this.boundItemDragStart);
    this.el.removeEventListener("dragover", this.boundDragOver, true);
    this.el.removeEventListener("drop", this.boundDrop, true);
    this.el.removeEventListener("dragleave", this.boundDragLeave, true);
    if (this.boundDocDragEnd) document.removeEventListener("dragend", this.boundDocDragEnd, true);
  }
}
